/**
 * LPO Gallery Fallback — shows the product's primary image when a selected
 * variant has no featured image (LPO gallery goes blank).
 *
 * Strategy:
 *  - After every variant change, wait for LPO to finish rendering, then check
 *    whether `.cl-po--gallery-wrapper` contains any visible images.
 *  - If the wrapper is blank, inject a fallback <img> as a sibling element
 *    AFTER the wrapper (before the accordion slot) and hide the empty wrapper
 *    using `visibility:hidden` (preserves layout, does NOT touch wrapper's
 *    children — avoids triggering LPO's own MutationObserver).
 *  - When a subsequent variant change produces a real image, remove the fallback
 *    and restore the wrapper's visibility.
 *
 * The product image URL is read from the `#ProductJson-*` script tag injected
 * by product-information.liquid (CloudLift compatibility fix).
 */
(function () {
  var SECTION_SEL = '.product-information';
  var MEDIA_COL_SEL = '.product-information__media';
  var LPO_SEL = '.cl-po--gallery-wrapper';
  var FALLBACK_CLASS = 'lpo-gallery-img-fallback';

  // ─── helpers ────────────────────────────────────────────────────────────────

  function getProductJson() {
    var el = document.querySelector('script[type="application/json"][id^="ProductJson-"]');
    if (!el) return null;
    try { return JSON.parse(el.textContent || el.innerText); } catch (_) { return null; }
  }

  /**
   * Returns the URL of the first usable product image.
   * Prefers `featured_image`, falls back to the `images` array.
   */
  function getPrimaryImageUrl(data) {
    if (!data) return null;
    var fi = data.featured_image;
    if (fi) return typeof fi === 'string' ? fi : (fi.src || null);
    var imgs = data.images;
    if (imgs && imgs.length) {
      var first = imgs[0];
      return typeof first === 'string' ? first : (first.src || null);
    }
    return null;
  }

  /**
   * Returns true when the LPO gallery wrapper contains no real images.
   * "Real" = an <img> with a non-empty src that doesn't look like a blank placeholder.
   */
  function isLpoGalleryBlank(lpoWrapper) {
    if (!lpoWrapper) return true;
    var imgs = lpoWrapper.querySelectorAll('img');
    for (var i = 0; i < imgs.length; i++) {
      var src = imgs[i].getAttribute('src') || imgs[i].getAttribute('data-src') || '';
      if (src && src !== '/' && src.indexOf('blank') === -1 && src.indexOf('placeholder') === -1) {
        return false;
      }
    }
    return true;
  }

  // ─── fallback element management ────────────────────────────────────────────

  /**
   * Ensure one `.lpo-gallery-img-fallback` div exists as a sibling
   * immediately AFTER `.cl-po--gallery-wrapper` in the media column.
   * (Never wraps / never reparents the LPO wrapper — avoids gallery reset.)
   */
  function ensureFallbackEl(mediaCol) {
    var existing = mediaCol.querySelector('.' + FALLBACK_CLASS);
    if (existing) return existing;

    var fb = document.createElement('div');
    fb.className = FALLBACK_CLASS;
    fb.style.cssText = 'display:none;width:100%;';

    var img = document.createElement('img');
    img.alt = '';
    img.style.cssText = 'display:block;width:100%;height:auto;';
    fb.appendChild(img);

    var lpoWrapper = mediaCol.querySelector(LPO_SEL);
    if (lpoWrapper) {
      lpoWrapper.insertAdjacentElement('afterend', fb);
    } else {
      mediaCol.appendChild(fb);
    }
    return fb;
  }

  // ─── main update logic ───────────────────────────────────────────────────────

  function update() {
    var sectionEl = document.querySelector(SECTION_SEL);
    if (!sectionEl) return;

    var mediaCol = sectionEl.querySelector(MEDIA_COL_SEL);
    if (!mediaCol) return;

    var lpoWrapper = mediaCol.querySelector(LPO_SEL);

    // LPO hasn't loaded yet — nothing to do
    if (!lpoWrapper) return;

    if (isLpoGalleryBlank(lpoWrapper)) {
      // Gallery is blank — show fallback, hide empty wrapper
      var productData = getProductJson();
      var imageUrl = getPrimaryImageUrl(productData);
      if (!imageUrl) return; // no product image available, give up

      var fb = ensureFallbackEl(mediaCol);
      var img = fb.querySelector('img');
      if (img && img.getAttribute('src') !== imageUrl) img.src = imageUrl;
      fb.style.display = '';
      lpoWrapper.style.visibility = 'hidden';
    } else {
      // Gallery has real images — restore wrapper, remove fallback
      lpoWrapper.style.removeProperty('visibility');
      var fallback = mediaCol.querySelector('.' + FALLBACK_CLASS);
      if (fallback) fallback.style.display = 'none';
    }
  }

  function scheduleUpdate() {
    // LPO renders asynchronously — check at multiple intervals
    setTimeout(update, 200);
    setTimeout(update, 600);
    setTimeout(update, 1400);
  }

  // ─── event listeners ─────────────────────────────────────────────────────────

  // LPO / theme variant events
  ['variant:change', 'variant:changed', 'product:variant-change', 'product:variant-changed'].forEach(function (ev) {
    document.addEventListener(ev, scheduleUpdate);
  });

  // Standard Shopify form change (covers option dropdowns / radio inputs)
  document.addEventListener('change', function (e) {
    var t = e.target;
    if (!(t instanceof Element)) return;
    if (
      t.matches('form[action*="/cart/add"] [name="id"]') ||
      t.matches('form[action*="/cart/add"] [name^="options"]') ||
      t.matches('[form] [name="id"]') ||
      t.matches('[form] [name^="options"]')
    ) {
      scheduleUpdate();
    }
  });

  // LPO swatches fire click events; run after a short settle time
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!(t instanceof Element)) return;
    if (t.closest('.cl-po--swatch, .cl-po--option, .cl-po--variant-option')) {
      scheduleUpdate();
    }
  });

  // ─── initial run ─────────────────────────────────────────────────────────────

  // The page may load directly on a no-image variant
  [400, 900, 1800].forEach(function (ms) { setTimeout(update, ms); });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', update);
  } else {
    update();
  }

  document.addEventListener('shopify:section:load', scheduleUpdate);
})();
